import { FC, useEffect, useState } from 'react';
import { Box, Flex, useToast, VStack, Stat, StatLabel, StatNumber, useColorModeValue, Center, Spinner, Text } from '@chakra-ui/react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, CartesianGrid } from 'recharts';
import { Panel, AnimatedCard, containerVariants, cardVariants } from '@bemap/ui';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getAnalystDashboard } from '../../services/analysts-service';
import { AnalystDashboardResult } from '@bemap/utils';

interface AssessmentData {
  month: string;
  total: number;
}

const MotionBox = motion(Box);

const AnalystDashboard: FC = () => {
  const toast = useToast();
  const { t } = useTranslation();
  const textColor = useColorModeValue('gray.800', 'white');
  const cardBg = useColorModeValue('white', 'gray.700');

  const initialState: AnalystDashboardResult = {
    credits: {
      totalCredits: 0,
      totalCreditsConsumed: 0,
      usage: {
        purchased: 0,
        used: 0,
        remaining: 0,
        monthlyUsage: [],
      },
      monthly: {
        months: [],
        creditsUsed: [],
      },
    },
    assessmentsStatus: {
      all: '0',
      waiting: '0',
      done: '0',
      generated: '0',
    },
    assessments: [],
    creditsNeeded: 0
  };

  const [data, setData] = useState<AnalystDashboardResult>(initialState);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { message } = await getAnalystDashboard();
        setData(message);
      } catch (err: any) {
        toast({
          title: t('general.error'),
          description: err.message || t('general.error'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Prepare data for LineChart
  const lineChartData = data.assessments.map((item: AssessmentData) => ({
    month: item.month,
    assessments: item.total,
    credits: item.total * 2.5,
  }));

  console.log('lineChartData', lineChartData);

  // Prepare data for PieChart (funnel)
  const pieData = [
    { name: t('analyst.dashboard_funnel_step1'), value: parseInt(data.assessmentsStatus.all) || 0, color: '#4299e1' },
    { name: t('analyst.dashboard_funnel_step2'), value: parseInt(data.assessmentsStatus.waiting) || 0, color: '#68D391' },
    { name: t('analyst.dashboard_funnel_step3'), value: parseInt(data.assessmentsStatus.done) || 0, color: '#ECC94B' },
    { name: t('analyst.dashboard_funnel_step4'), value: parseInt(data.assessmentsStatus.generated || '0') || 0, color: '#F56565' },
  ];

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!data) {
    return (
      <Center h="400px">
        <Text>{t('general.no_data_available')}</Text>
      </Center>
    );
  }

  return (
    <MotionBox
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      as={VStack}
      gap='20px'
      align="stretch"
    >
      {/* Statistics Cards */}
      <Flex gap='20px' mt={'20px'} justifyContent='flex-start' w={'100%'} flexWrap="wrap">
        <AnimatedCard p='15px' w={'100%'} maxW="300px" bg={cardBg} variants={cardVariants}>
          <Stat>
            <StatLabel color={textColor} fontSize="sm">
              {t('analyst.dashboard_metrics_credits_available')}
            </StatLabel>
            <StatNumber color={textColor} fontSize="2xl">
              {data.credits.totalCredits || 0}
            </StatNumber>
          </Stat>
        </AnimatedCard>
        <AnimatedCard p='15px' w={'100%'} maxW="300px" bg={cardBg} variants={cardVariants}>
          <Stat>
            <StatLabel color={textColor} fontSize="sm">
              {t('analyst.dashboard_metrics_credits_consumed')}
            </StatLabel>
            <StatNumber color={textColor} fontSize="2xl">
              {data.credits.totalCreditsConsumed || 0}
            </StatNumber>
          </Stat>
        </AnimatedCard>
        <AnimatedCard p='15px' w={'100%'} maxW="300px" bg={cardBg} variants={cardVariants}>
          <Stat>
            <StatLabel color={textColor} fontSize="sm">
              {t('analyst.dashboard_metrics_credits_needs')}
            </StatLabel>
            <StatNumber color={textColor} fontSize="2xl">
              {data.creditsNeeded - data.credits.totalCredits || 0}
            </StatNumber>
          </Stat>
        </AnimatedCard>
      </Flex>

      {/* Charts Row */}
      <Flex direction={{ base: 'column', lg: 'row' }} w='100%' gap={'20px'} >
        <VStack gap={4} w={'100%'} alignItems='stretch'>
          <Text mt='20px' color={textColor} fontSize='22px' fontWeight='700' lineHeight='100%'>
            {t('analyst.dashboard_main_chart_title')}
          </Text>
          <Panel padding={4}>
            {lineChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minHeight="350px">
                <LineChart data={lineChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="assessments"
                    stroke="#4299e1"
                    strokeWidth={2}
                    name="Assessments"
                  />
                  <Line
                    type="monotone"
                    dataKey="credits"
                    stroke="#68D391"
                    strokeWidth={2}
                    name="Credits"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Center h="350px">
                <Text color={textColor}>No data available for chart</Text>
              </Center>
            )}
          </Panel>
        </VStack>

        <VStack gap={4} w={'100%'} alignItems='stretch'>
          <Text mt='20px' color={textColor} fontSize='22px' fontWeight='700' lineHeight='100%'>
            {t('analyst.dashboard_funnel_title')}
          </Text>
          <Panel padding={4}>
            {pieData.some(item => item.value > 0) && parseInt(data.assessmentsStatus.all) > 0 ? (
              <ResponsiveContainer width="100%" height="100%" minHeight="350px">
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={(props) => {
                      const { name, percent } = props as { name?: string; percent?: number };
                      return `${name ?? ''} ${(percent ? percent * 100 : 0).toFixed(0)}%`;
                    }}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Center h="350px">
                <Text color={textColor}>No data available for chart</Text>
              </Center>
            )}
          </Panel>
        </VStack>
      </Flex>
    </MotionBox>
  );
};

export default AnalystDashboard; 